'use client';

import { memo, useEffect, useRef, useState } from 'react';
import {
  ChevronLeft, ChevronRight, Sparkles, Clapperboard, Play,
  Instagram, Youtube, Music2, Infinity as InfinityIcon,
} from 'lucide-react';
import { cn, getOptimizedPhotoUrl } from '@/lib/utils';
import type { PortfolioVideoEntry } from '@/lib/types/portfolio-video';
import { VideoEmbed } from './video-embed';
// Singleton partagé — même cache module-level que HeroVideoCarousel,
// mais indépendant pour éviter l'import circulaire. Les deux composants
// étant dans la même page, l'un des deux resolve immédiatement depuis le
// cache navigateur (max-age=300) si l'autre a déjà demandé.
let _vs_promise: Promise<PortfolioVideoEntry[]> | null = null;
function _portfolioVideosFetch(): Promise<PortfolioVideoEntry[]> {
  if (!_vs_promise) {
    _vs_promise = fetch('/api/public/portfolio-videos')
      .then(r => r.ok ? r.json() : [])
      .catch(() => []);
  }
  return _vs_promise;
}


import { Reveal } from './Reveal';

// ---------------------------------------------------------------------------
// Placeholders — uniquement pour compléter la rangée si peu de vraies vidéos
// sont disponibles. Même chrome que les vraies cartes (pas de rotation, pas
// de dégradés criards) pour que l'ensemble se lise comme un tout cohérent.
// ---------------------------------------------------------------------------

const PLACEHOLDER_LABELS = [
  "Beauty", "B2B", "Animaux", "Mode", "Lifestyle", "Sport", "Food", "Santé",
];

// Tuiles plateformes façon icônes d'app : squircle aux couleurs de la marque
// + glow assorti, le nom apparaît au survol.
const PLATFORMS = [
  { name: "Meta",      icon: InfinityIcon, tile: "bg-[#0866FF] shadow-[#0866FF]/35" },
  { name: "TikTok",    icon: Music2,       tile: "bg-black shadow-black/30" },
  { name: "Instagram", icon: Instagram,    tile: "bg-gradient-to-br from-[#F9CE34] via-[#EE2A7B] to-[#6228D7] shadow-[#EE2A7B]/35" },
  { name: "YouTube",   icon: Youtube,      tile: "bg-[#FF0000] shadow-[#FF0000]/35" },
];

const MIN_CARDS = 10;

// ---------------------------------------------------------------------------
// useInView — ne joue la vidéo qu'une fois la carte entrée dans le viewport
// ---------------------------------------------------------------------------

function useInView<T extends HTMLElement>(rootMargin = '300px') {
  const ref = useRef<T | null>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || inView) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { rootMargin }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [inView, rootMargin]);

  return { ref, inView };
}

// ---------------------------------------------------------------------------
// Card shell — même gabarit pour une vraie vidéo ou un placeholder
// ---------------------------------------------------------------------------

const CARD_SHELL = "relative flex-shrink-0 w-44 sm:w-52 lg:w-56 rounded-[1.5rem] overflow-hidden shadow-xl snap-center transition-transform duration-300 hover:scale-[1.02]";

function RealVideoCard({ entry }: { entry: PortfolioVideoEntry }) {
  const { ref, inView } = useInView<HTMLDivElement>();
  const [loaded, setLoaded] = useState(false);
  const firstName = entry.creator?.name.split(' ')[0];
  const avatarUrl = entry.creator ? getOptimizedPhotoUrl(entry.creator.photoUrl, 'small') : undefined;

  return (
    <div ref={ref} className={cn(CARD_SHELL, "bg-muted")} style={{ aspectRatio: "9/16" }}>
      {!loaded && (
        <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-primary/50 to-secondary/50 animate-pulse">
          <Clapperboard className="h-8 w-8 text-white/50" />
        </div>
      )}
      <VideoEmbed
        entry={entry}
        shouldPlay={inView}
        onReady={() => setLoaded(true)}
        className={cn(
          "absolute inset-0 w-full h-full object-cover transition-opacity duration-500",
          loaded ? "opacity-100" : "opacity-0"
        )}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-black/10 pointer-events-none" />
      <div className="absolute top-4 left-4">
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-black/50 backdrop-blur-sm text-white text-[11px] font-semibold tracking-wide uppercase">
          <Sparkles className="h-3 w-3" /> {entry.category ?? "Vidéo phare"}
        </span>
      </div>
      {entry.creator && (
        <div className="absolute bottom-4 left-4 flex items-center gap-2">
          {avatarUrl && (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              src={avatarUrl}
              alt={firstName}
              loading="lazy"
              decoding="async"
              className="w-7 h-7 rounded-full object-cover border border-white/60"
            />
          )}
          <span className="text-white text-sm font-semibold drop-shadow">{firstName}</span>
        </div>
      )}
    </div>
  );
}

function PlaceholderCard({ label }: { label: string }) {
  return (
    <div
      className={cn(CARD_SHELL, "bg-gradient-to-br from-primary to-secondary flex items-center justify-center")}
      style={{ aspectRatio: "9/16" }}
    >
      <Clapperboard className="h-10 w-10 text-white/40" />
      <div className="absolute bottom-4 left-4">
        <span className="inline-block px-2.5 py-1 rounded-full bg-black/30 backdrop-blur-sm text-white text-[11px] font-semibold tracking-wide uppercase">
          {label}
        </span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ShowcaseRow — une seule rangée de grandes cartes, vraies vidéos d'abord
// ---------------------------------------------------------------------------

const AUTO_SCROLL_PX_PER_SEC = 90;

type ShowcaseCard =
  | { kind: 'real'; entry: PortfolioVideoEntry }
  | { kind: 'placeholder'; label: string };

function ShowcaseRow({ videos }: { videos: PortfolioVideoEntry[] }) {
  const trackRef = useRef<HTMLDivElement>(null);
  const padCount = Math.max(0, MIN_CARDS - videos.length);

  // Pas de duplication de contenu : une vraie vidéo dupliquée donnerait deux
  // instances d'iframe/vidéo indépendantes et non synchronisées, visible comme
  // un décrochage/saut au moment du bouclage. À la place, le défilement avance
  // toujours dans le même sens ; dès qu'une carte sort complètement à gauche,
  // on la déplace (déplacement DOM réel, pas de recréation) à la fin de la
  // rangée et on compense scrollLeft d'exactement sa largeur dans le même
  // tick — la carte (et son iframe) ne sont jamais détruits, juste réordonnés,
  // et le décalage de scrollLeft rend l'opération invisible à l'œil.
  const cards: ShowcaseCard[] = [
    ...videos.map((entry): ShowcaseCard => ({ kind: 'real', entry })),
    ...Array.from({ length: padCount }, (_, i): ShowcaseCard => ({
      kind: 'placeholder',
      label: PLACEHOLDER_LABELS[i % PLACEHOLDER_LABELS.length],
    })),
  ];

  const drag = useRef({ active: false, startX: 0, startScrollLeft: 0, moved: false });
  const autoScroll = useRef({ paused: false });
  const resumeTimeout = useRef<number | undefined>(undefined);

  // scroll-snap-type doit être désactivé pendant l'auto-scroll : sinon le
  // navigateur re-snap en continu vers le point le plus proche à chaque micro-
  // incrément de scrollLeft, ce qui annule visuellement tout mouvement.
  function setSnapEnabled(enabled: boolean) {
    const track = trackRef.current;
    if (track) track.style.scrollSnapType = enabled ? 'x mandatory' : 'none';
  }

  function scrollByCards(direction: 1 | -1) {
    setSnapEnabled(true);
    pauseAutoScrollFor(2500);
    trackRef.current?.scrollBy({ left: direction * 360, behavior: 'smooth' });
  }

  function pauseAutoScrollFor(ms: number) {
    autoScroll.current.paused = true;
    window.clearTimeout(resumeTimeout.current);
    resumeTimeout.current = window.setTimeout(() => {
      autoScroll.current.paused = false;
    }, ms);
  }

  // Défilement automatique doux — en pause au survol/interaction, jamais sous prefers-reduced-motion.
  // setInterval plutôt que requestAnimationFrame : ce dernier peut être fortement
  // throttlé sans compositeur actif (onglet en arrière-plan, rendu headless).
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const TICK_MS = 30;
    const stepPx = (AUTO_SCROLL_PX_PER_SEC * TICK_MS) / 1000;

    const intervalId = window.setInterval(() => {
      if (autoScroll.current.paused || drag.current.active) return;
      if (track.scrollWidth <= track.clientWidth) return;
      setSnapEnabled(false);
      track.scrollLeft += stepPx;

      // Dès que la première carte est entièrement sortie à gauche, on la
      // déplace à la fin (vrai nœud DOM, pas de recréation) et on compense
      // scrollLeft d'exactement sa largeur — invisible à l'œil, jamais de saut.
      // Le seuil se limite au maxScroll natif : si le contenu total est plus
      // étroit qu'une carte (peu de vidéos), scrollLeft plafonne avant
      // d'atteindre firstWidth et resterait bloqué sans ce plafond.
      const first = track.firstElementChild as HTMLElement | null;
      if (first) {
        const gap = parseFloat(getComputedStyle(track).columnGap || '0');
        const firstWidth = first.getBoundingClientRect().width + gap;
        const maxScroll = track.scrollWidth - track.clientWidth;
        if (track.scrollLeft >= Math.min(firstWidth, maxScroll)) {
          track.appendChild(first);
          track.scrollLeft -= firstWidth;
        }
      }
    }, TICK_MS);

    return () => window.clearInterval(intervalId);
  }, []);

  // Glisser à la souris (le tactile/trackpad utilise déjà le scroll natif).
  function onPointerDown(e: React.PointerEvent) {
    if (e.pointerType !== 'mouse') return;
    const track = trackRef.current;
    if (!track) return;
    setSnapEnabled(true);
    drag.current = { active: true, startX: e.clientX, startScrollLeft: track.scrollLeft, moved: false };
    track.setPointerCapture(e.pointerId);
    autoScroll.current.paused = true;
  }

  function onPointerMove(e: React.PointerEvent) {
    if (!drag.current.active) return;
    const track = trackRef.current;
    if (!track) return;
    const delta = e.clientX - drag.current.startX;
    if (Math.abs(delta) > 3) drag.current.moved = true;
    track.scrollLeft = drag.current.startScrollLeft - delta;
  }

  function onPointerUp(e: React.PointerEvent) {
    if (!drag.current.active) return;
    drag.current.active = false;
    trackRef.current?.releasePointerCapture(e.pointerId);
    pauseAutoScrollFor(2500);
  }

  return (
    <div
      className="relative overflow-hidden group/vid"
      onMouseEnter={() => { setSnapEnabled(true); autoScroll.current.paused = true; }}
      onMouseLeave={() => { if (!drag.current.active) autoScroll.current.paused = false; }}
    >
      <div className="pointer-events-none absolute inset-y-0 left-0 w-16 sm:w-28 z-10 bg-gradient-to-r from-background to-transparent" />
      <div className="pointer-events-none absolute inset-y-0 right-0 w-16 sm:w-28 z-10 bg-gradient-to-l from-background to-transparent" />

      <button
        type="button"
        onClick={() => scrollByCards(-1)}
        aria-label="Vidéos précédentes"
        className="hidden sm:flex absolute left-4 top-1/2 -translate-y-1/2 z-20 w-11 h-11 items-center justify-center rounded-full bg-background/90 backdrop-blur-sm border shadow-lg hover:bg-background transition-colors"
      >
        <ChevronLeft className="h-5 w-5" />
      </button>
      <button
        type="button"
        onClick={() => scrollByCards(1)}
        aria-label="Vidéos suivantes"
        className="hidden sm:flex absolute right-4 top-1/2 -translate-y-1/2 z-20 w-11 h-11 items-center justify-center rounded-full bg-background/90 backdrop-blur-sm border shadow-lg hover:bg-background transition-colors"
      >
        <ChevronRight className="h-5 w-5" />
      </button>

      <div
        ref={trackRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        className="flex gap-6 sm:gap-8 py-6 px-6 sm:px-16 overflow-x-auto snap-x snap-mandatory scrollbar-hide cursor-grab active:cursor-grabbing select-none"
      >
        {cards.map((card, i) => (
          card.kind === 'real'
            ? <RealVideoCard key={`c-${i}`} entry={card.entry} />
            : <PlaceholderCard key={`c-${i}`} label={card.label} />
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// VideoShowcase — composant exporté
// ---------------------------------------------------------------------------

// memo() : ShowcaseRow réordonne ses cartes en manipulant le DOM directement
// (voir plus haut) pour éviter de recréer les iframes des vraies vidéos. Un
// re-render déclenché par un état sans rapport plus haut dans l'arbre (ex:
// la frappe dans le formulaire de contact) reconstruirait le DOM depuis le
// dernier rendu React connu et annulerait ce réordonnancement — memo() bloque
// ces re-renders tant qu'aucune prop ne change (il n'y en a aucune ici).
function VideoShowcase() {
  const [liveVideos, setLiveVideos] = useState<PortfolioVideoEntry[]>([]);

  useEffect(() => {
    _portfolioVideosFetch()
      .then(setLiveVideos)
      .catch(() => {});
  }, []);

  return (
    <section id="creations" className="relative py-14 sm:py-20 bg-muted/20 overflow-hidden">
      <div
        aria-hidden="true"
        className="absolute -top-24 left-1/2 -translate-x-1/2 w-[36rem] h-[36rem] rounded-full bg-gradient-to-br from-primary/10 to-secondary/10 blur-3xl pointer-events-none"
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-8 sm:mb-10">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
          <div className="max-w-2xl">
            <Reveal>
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary/10 text-sm font-semibold text-primary mb-4">
                <Sparkles className="h-3.5 w-3.5" /> Nos créations
              </span>
            </Reveal>
            <Reveal delayMs={100}>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.15]">
                Des vidéos conçues
                <br className="hidden sm:block" /> pour{" "}
                {/* Surlignage marqueur : bloc dégradé animé derrière le mot */}
                <span className="relative inline-block px-2.5 sm:px-3">
                  <span
                    aria-hidden="true"
                    className="absolute inset-x-0 inset-y-0.5 -rotate-1 rounded-lg sm:rounded-xl bg-gradient-to-r from-primary to-secondary bg-[length:200%_auto] animate-[gradientX_5s_ease-in-out_infinite]"
                  />
                  <span className="relative text-white">vendre</span>
                </span>
              </h2>
            </Reveal>
            <Reveal delayMs={200}>
              <p className="mt-4 max-w-md text-lg leading-relaxed text-muted-foreground">
                Pas pour impressionner. Du hook à l&apos;appel à l&apos;action,
                chaque seconde est pensée pour convertir.
              </p>
            </Reveal>
          </div>
          <Reveal delayMs={200} className="flex flex-col items-center gap-5 lg:items-end lg:pr-4">
            <p className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground/60">
              Prêtes à diffuser sur
            </p>
            <span className="sr-only">Meta, TikTok, Instagram et YouTube</span>
            {/* Orbite : les 4 plateformes tournent lentement autour du hub ;
                chaque icône contre-tourne à la même vitesse pour rester
                droite. Pause au survol, statique si prefers-reduced-motion. */}
            <div className="group relative h-44 w-44" aria-hidden="true">
              <div className="absolute inset-0 rounded-full border-2 border-dashed border-border/80" />
              <div className="absolute inset-0 m-auto flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-primary to-secondary shadow-lg">
                <Play className="ml-0.5 h-5 w-5 text-white" />
              </div>
              <div className="absolute inset-0 animate-[spinSlow_24s_linear_infinite] group-hover:[animation-play-state:paused] motion-reduce:animate-none">
                {PLATFORMS.map((p, i) => (
                  <div
                    key={p.name}
                    className="absolute left-1/2 top-1/2 -ml-6 -mt-6 h-12 w-12"
                    style={{ transform: `rotate(${i * 90}deg) translateY(-88px)` }}
                  >
                    <div style={{ transform: `rotate(${-i * 90}deg)` }}>
                      <span
                        title={p.name}
                        className={`flex h-12 w-12 items-center justify-center rounded-2xl shadow-lg animate-[spinSlowReverse_24s_linear_infinite] group-hover:[animation-play-state:paused] motion-reduce:animate-none ${p.tile}`}
                      >
                        <p.icon className="h-5 w-5 text-white" strokeWidth={2.2} />
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </div>

      <div className="relative">
        <ShowcaseRow videos={liveVideos} />
      </div>
    </section>
  );
}

export default memo(VideoShowcase);
